import { useMemo } from 'react';
import React from 'react';
import { StyleProp, StyleSheet, View, ViewStyle } from 'react-native';

import { Text, tokens } from '@fhs/ui';

import { useRewardTiers } from '../queries/loyalty.queries';

import { useLoyaltyLegacyStates } from './use-loyalty-legacy-states';


const POINTS_THRESHOLD_FOR_ALMOST_THERE = 500;

interface TierProgressAlertData {
  type: 'almost-there' | 'tier-reached';
  pointsAway: number;
  tierName: string;
}

export function useTierProgressAlert() {
  const { data: rewardTiers, isLoading: isLoadingRewardTiers } = useRewardTiers();
  const { loyaltyUser, loyaltyUserReady } = useLoyaltyLegacyStates();

  const tierProgressAlert = useMemo<TierProgressAlertData | null>(() => {
    if (!rewardTiers || !loyaltyUserReady || typeof loyaltyUser?.points !== 'number') {
      return null;
    }

    const userPoints = loyaltyUser.points;
    const sortedTiers = [...rewardTiers].sort((a, b) => a.points - b.points);

    // Find the tier that user just reached (within the threshold) or is about to reach
    for (const tier of sortedTiers) {
      const pointsAway = tier.points - userPoints;

      // User has reached this tier (points >= tier.points) but is within the threshold from the tier
      // This means they just crossed this tier threshold
      if (pointsAway <= 0 && pointsAway > -POINTS_THRESHOLD_FOR_ALMOST_THERE) {
        return {
          type: 'tier-reached',
          pointsAway: 0,
          tierName: tier.name,
        };
      }

      // User is close to reaching this tier (within threshold)
      if (pointsAway > 0 && pointsAway <= POINTS_THRESHOLD_FOR_ALMOST_THERE) {
        return {
          type: 'almost-there',
          pointsAway,
          tierName: tier.name,
        };
      }
    }

    return null;
  }, [rewardTiers, loyaltyUser?.points, loyaltyUserReady]);

  const isLoading = isLoadingRewardTiers || !loyaltyUserReady;

  return {
    tierProgressAlert,
    isLoading,
  };
}

interface TierProgressAlertProps {
  style?: StyleProp<ViewStyle>;
}

export function TierProgressAlert({ style }: TierProgressAlertProps) {
  const { tierProgressAlert, isLoading } = useTierProgressAlert();

  if (isLoading || !tierProgressAlert) {
    return null;
  }

  const emoji = tierProgressAlert.type === 'almost-there' ? '⚡' : '🎉';

  return (
    <View style={[styles.container, style]}>
      <Text style={styles.emoji}>{emoji}</Text>
      <View style={styles.messageContainer}>
        {tierProgressAlert.type === 'almost-there' ? (
          <Text.Ui size="sm" style={styles.messageText}>
            You're almost there! You are only {tierProgressAlert.pointsAway} points away from
            getting a{' '}
            <Text.Ui size="sm" weight="semibold">
              {tierProgressAlert.tierName}
            </Text.Ui>
            !
          </Text.Ui>
        ) : (
          <Text.Ui size="sm" style={styles.messageText}>
            Congratulations! You can now redeem a{' '}
            <Text.Ui size="sm" weight="semibold">
              {tierProgressAlert.tierName}
            </Text.Ui>
            ! Keep going and get more rewards.
          </Text.Ui>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: tokens.colors.$houseLight,
  },
  messageContainer: {
    flex: 1,
  },
  messageText: {
    lineHeight: 16.8,
  },
  emoji: {
    fontSize: 32,
    lineHeight: 44,
  },
});
